import React, { useEffect, useRef, forwardRef, useImperativeHandle, useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Grid from '@material-ui/core/Grid';
import CircularProgress from '@material-ui/core/CircularProgress';
import Axios from 'axios';
import { apiATC } from '../../../config';
import { getListeSertisseur } from '../../../actions';

const SertisseurEnvoiForm = forwardRef(({ listeSertisseurs, getListeSertisseur, onSubmitForm, dataForm, onOfChange, isOfOnly = false, isFormOnly = false, selectedOf = null, hideSubmitButton = false, onValidityChange, onHistoriqueFetched }, ref) => {
    const sertisseurRef = useRef(null);
    const ofRef = useRef(null);
    const poidsRef = useRef(null);
    const [sertisseurInput, setSertisseurInput] = useState('');
    const [ofInputValue, setOfInputValue] = useState('');
    const [loadingOf, setLoadingOf] = useState(false);
    const [ofError, setOfError] = useState(null);
    const [hasSubmitted, setHasSubmitted] = useState(false);

    const filteredSertisseurs = useMemo(
        () => listeSertisseurs
            .filter(r => r.actif === 1)
            .filter(r => r.label?.toLowerCase().includes(sertisseurInput.toLowerCase())),
        [listeSertisseurs, sertisseurInput]
    );

    // Fonction pour récupérer les détails du mouvement via l'API
    const fetchMovementDetails = async (numSachet, isAtelierMode = false) => {
        try {
            setLoadingOf(true);
            const action = isAtelierMode ? 'detail_atelier' : 'detail_serti';
            const response = await Axios.post(`${apiATC}/atelier/mouvements_atelier.php`, { of: numSachet, action });
            const resultData = response.data;

            if (resultData && Array.isArray(resultData)) {
                const ref_cv = resultData[0]?.ref_cv || '';
                const ref = resultData[0]?.ref || '';
                
                if (onHistoriqueFetched) {
                    onHistoriqueFetched(resultData, numSachet, isAtelierMode);
                }
                
                return { data: resultData, ref_cv, ref };
            } else if (resultData?.error) {
                console.error('Erreur API:', resultData.error);
                return resultData;
            }
            return resultData;
        } catch (error) {
            console.error('Erreur lors de la récupération des détails:', error);
            return null;
        } finally {
            setLoadingOf(false);
        }
    };

    const formik = useFormik({
        initialValues: {
            sertisseur: null,
            of: '',
            poids: '',
        },
        validationSchema: isOfOnly 
            ? Yup.object().shape({
                of: Yup.string().required('N° OF obligatoire.'),
            })
            : Yup.object().shape({
                of: isFormOnly ? Yup.string().nullable() : Yup.string().required('N° OF obligatoire.'),
                sertisseur: Yup.object().required('Sertisseur obligatoire.'),
                poids: Yup.number()
                    .typeError('Le poids doit être un nombre')
                    .positive('Le poids doit être positif')
                    .required('Poids obligatoire')
                    .nullable(),
            }),
        validateOnChange: false,
        validateOnBlur: false,
        onSubmit: (values, { resetForm }) => {
            if (isOfOnly) return;
            const newValues = { ...values, of: selectedOf, id_sert: dataForm?.id_sert ?? null }
            if (onSubmitForm) {
                onSubmitForm(newValues);
            }
            resetForm({
                values: {
                    sertisseur: values.sertisseur,
                    of: '',
                    poids: '',
                },
                touched: {},
                errors: {}
            });
            setHasSubmitted(false);
        },
    });

    const { values, errors, handleChange, handleSubmit, handleBlur, setFieldValue } = formik;

    const handleFormSubmit = (event) => {
        event.preventDefault();
        setHasSubmitted(true);
        handleSubmit(event);
    };

    useImperativeHandle(ref, () => ({
        focusSertisseur: () => sertisseurRef.current?.focus(),
        focusOfField: () => {
            if (ofRef.current) {
                ofRef.current.focus();
                ofRef.current.select();
            }
        },
        clearAndFocusOf: () => {
            setFieldValue('of', '');
            setOfInputValue('');
            setFieldValue('poids', '');
            setHasSubmitted(false);
            if (ofRef.current) {
                setTimeout(() => ofRef.current?.focus(), 50);
            }
        },
        submitForm: () => {
            setHasSubmitted(true);
            handleSubmit();
        },
        refetchHistorique: async (numSachetOverride) => {
            const selectedNum = numSachetOverride
                || (typeof selectedOf === 'object' && selectedOf?.num_sachet ? selectedOf.num_sachet : null)
                || values.of
                || ofInputValue.trim();

            if (!selectedNum) {
                return null;
            }

            return fetchMovementDetails(selectedNum, false);
        },
    }));

    useEffect(() => {
        getListeSertisseur();
    }, [getListeSertisseur]);

    useEffect(() => {
        if (values.sertisseur && poidsRef.current && !isFormOnly) {
            poidsRef.current.focus();
        }
    }, [values.sertisseur, isFormOnly]);

    // Notifier le parent de la validité du formulaire
    useEffect(() => {
        if (onValidityChange && !isOfOnly) {
            const isValid = values.sertisseur && values.poids && !errors.sertisseur && !errors.poids;
            onValidityChange(isValid);
        }
    }, [values.sertisseur, values.poids, errors.sertisseur, errors.poids, onValidityChange, isOfOnly]);

    return (
        <form onSubmit={handleFormSubmit}>
            <Grid container spacing={2}>
                {/* N° OF - Affiché seulement si isOfOnly ou (!isOfOnly && !isFormOnly) */}
                {!isFormOnly && (
                <Grid item xs={12}>
                    <TextField
                        label="N° OF"
                        variant="outlined"
                        fullWidth
                        value={ofInputValue}
                        onChange={(e) => {
                            setOfInputValue(e.target.value);
                            setOfError(null);
                        }}
                        inputRef={ofRef}
                        autoFocus={!isFormOnly}
                        error={(hasSubmitted && !!errors.of) || !!ofError}
                        helperText={ofError || (hasSubmitted && errors.of ? errors.of : ' ')}
                        disabled={loadingOf}
                        onFocus={(e) => e.target.select()}
                        onClick={(e) => e.target.select()}
                        onKeyDown={async (e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                setOfError(null);
                                const numSachet = ofInputValue.trim();
                                if (!numSachet) {
                                    setOfError('N° OF obligatoire');
                                    return;
                                }

                                setFieldValue('of', numSachet);

                                if (onOfChange) {
                                    onOfChange(numSachet, null, null, { reset: true });
                                }

                                const apiResponse = await fetchMovementDetails(numSachet, false);
                                
                                if (apiResponse?.error) {
                                    setOfError(apiResponse.error || 'Erreur lors de la récupération de l\'OF');
                                    setTimeout(() => {
                                        if (ofRef.current) {
                                            ofRef.current.focus();
                                            ofRef.current.select();
                                        }
                                    }, 50);
                                    return;
                                }

                                const ref_cv = apiResponse?.ref_cv || null;
                                const ref = apiResponse?.ref || null;
                                
                                if (onOfChange) {
                                    onOfChange(numSachet, null, { 
                                        num_sachet: numSachet,
                                        ref: ref_cv,
                                        refDesc: ref
                                    });
                                }
                            }
                        }}
                        InputProps={{
                            endAdornment: loadingOf ? <CircularProgress color="inherit" size={20} /> : null,
                        }}
                    />
                </Grid>
                )}

                {/* Sertisseur et Poids - Affichés uniquement si !isOfOnly */}
                {!isOfOnly && (
                    <>
                        {/* Sertisseur */}
                        <Grid item xs={12}>
                            <Autocomplete
                                options={listeSertisseurs.filter(r => r.actif === 1)}
                                getOptionLabel={(option) => option.label}
                                value={values.sertisseur}
                                onChange={(event, newValue) => {
                                    setFieldValue('sertisseur', newValue);
                                    setSertisseurInput(newValue ? newValue.label : '');
                                }}
                                inputValue={sertisseurInput}
                                onInputChange={(event, newInputValue) => setSertisseurInput(newInputValue || '')}
                                autoComplete
                                fullWidth
                                clearOnEscape
                                getOptionSelected={(option, values) => option.id === values.id}
                                renderInput={(params) => <TextField {...params} label="Sertisseur" variant="outlined" fullWidth
                                    inputRef={sertisseurRef}
                                    error={hasSubmitted && !!errors.sertisseur}
                                    helperText={hasSubmitted && errors.sertisseur ? errors.sertisseur : ' '}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            const candidate = filteredSertisseurs[0];
                                            if (candidate) {
                                                setFieldValue('sertisseur', candidate);
                                                setTimeout(() => poidsRef.current?.focus(), 0);
                                            }
                                        }
                                    }} />}
                            />
                        </Grid>

                        {/* Poids */}
                        <Grid item xs={12}>
                            <TextField
                                label="Poids"
                                variant="outlined"
                                value={values.poids}
                                fullWidth
                                onChange={handleChange}
                                onBlur={handleBlur}
                                name="poids"
                                error={hasSubmitted && !!errors.poids}
                                helperText={hasSubmitted && errors.poids ? errors.poids : ' '}
                                inputRef={poidsRef}
                                onKeyPress={async (e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        setHasSubmitted(true);
                                        const errors = await formik.validateForm();
                                        formik.setFieldTouched('poids');
                                        if (!errors.poids && !errors.sertisseur && (isFormOnly || !errors.of)) {
                                            formik.handleSubmit();
                                        }
                                    }
                                }}
                            />
                        </Grid>

                        {/* Bouton Ajouter */}
                        <Grid item xs={12} style={{ display: hideSubmitButton ? 'none' : 'flex', justifyContent: 'center' }}>
                            <Button
                                variant="contained"
                                color="primary"
                                type="submit"
                                onClick={() => setHasSubmitted(true)}
                                style={{ 
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    fontSize: '0.9375rem',
                                    borderRadius: '8px',
                                    padding: '8px 24px',
                                    boxShadow: 'none'
                                }}>
                                Ajouter
                            </Button>
                        </Grid>
                    </>
                )}
            </Grid>
        </form>
    );
});

SertisseurEnvoiForm.displayName = 'SertisseurEnvoiForm';

const mapStateToProps = (state) => {
    return {
        listeSertisseurs: state.generalData.listeSertisseur,
    };
};

const mapDispatchToProps = {
    getListeSertisseur,
};

export default connect(mapStateToProps, mapDispatchToProps, null, { forwardRef: true })(SertisseurEnvoiForm);
